/**
 * Centralized Error Logger Service
 *
 * Features:
 * - Component-specific logging that can be toggled on/off
 * - All logs go to database, NOTHING to console in production
 * - User sees only generic error messages
 * - Full debug info stored in database
 */

// Removed api import to avoid circular dependency - using native fetch instead

interface LogEntry {
  component: string;
  level: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  message: string;
  details?: any;
  userId?: number;
  customerId?: number;
  metadata?: any;
  timestamp?: string;
  url?: string;
  userAgent?: string;
}

interface ComponentDebugSettings {
  [component: string]: boolean;
}

class ErrorLoggerService {
  private debugSettings: ComponentDebugSettings = {};
  private isInitialized = false;
  private isDevelopment = process.env.NODE_ENV === 'development';
  private logQueue: LogEntry[] = [];
  private isProcessingQueue = false;
  private batchTimer: NodeJS.Timeout | null = null;
  private errorNotificationCallback: ((message: string) => void) | null = null;
  private consoleOutputEnabled = false; // Disable console output even in development
  private isLoggingOut = false; // Flag to suppress notifications during logout

  /**
   * Initialize the error logger by fetching debug settings from the server
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Use native fetch to avoid circular dependency
      const apiUrl = process.env.REACT_APP_API_URL || '/api/v1';
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      // Only fetch debug settings if we have a token
      if (token) {
        const response = await fetch(`${apiUrl}/endpoints/debug-settings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            this.debugSettings = data.settings || {};
          }
        }
      }

      this.isInitialized = true;

      // Override console methods in production
      if (!this.isDevelopment) {
        this.overrideConsoleMethods();
      }
    } catch (error) {
      // Silently fail - use default settings
      this.isInitialized = true;
    }
  }

  /**
   * Override console methods to prevent any console output in production
   */
  private overrideConsoleMethods() {
    const noop = () => {};

    // Save original methods for internal use only
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
      debug: console.debug
    };

    // Override all console methods with no-op in production
    console.log = noop;
    console.error = noop;
    console.warn = noop;
    console.info = noop;
    console.debug = noop;
    console.trace = noop;
    console.group = noop;
    console.groupEnd = noop;
    console.table = noop;
    console.time = noop;
    console.timeEnd = noop;

    // Store original console for emergency use (never expose to components)
    (window as any).__originalConsole = originalConsole;
  }

  /**
   * Set a callback for user error notifications
   */
  setErrorNotificationCallback(callback: ((message: string) => void) | null) {
    this.errorNotificationCallback = callback;
  }

  /**
   * Check if logging is enabled for a component
   */
  private isComponentEnabled(component: string): boolean {
    // In development, always log
    if (this.isDevelopment) return true;

    // Check debug settings
    return this.debugSettings[component] !== false;
  }

  /**
   * Sanitize log entry to prevent payload size issues
   */
  private sanitizeLogEntry(entry: LogEntry): LogEntry {
    const MAX_FIELD_SIZE = 2000; // 2KB per field (reduced from 5KB)
    const MAX_TOTAL_SIZE = 10000; // 10KB per entry (reduced from 50KB)

    const truncateString = (str: string, maxLength: number): string => {
      if (str.length <= maxLength) return str;
      return str.substring(0, maxLength) + '... [truncated]';
    };

    const sanitizeObject = (obj: any, depth = 0): any => {
      if (depth > 5) return '[max depth]';
      if (obj === null || obj === undefined) return obj;

      if (typeof obj === 'string') {
        return truncateString(obj, MAX_FIELD_SIZE);
      }

      if (Array.isArray(obj)) {
        // Limit array size
        return obj.slice(0, 50).map(item => sanitizeObject(item, depth + 1));
      }

      if (typeof obj === 'object') {
        const sanitized: any = {};
        for (const [key, value] of Object.entries(obj)) {
          sanitized[key] = sanitizeObject(value, depth + 1);
        }
        return sanitized;
      }

      return obj;
    };

    const sanitized = {
      ...entry,
      message: truncateString(entry.message, MAX_FIELD_SIZE),
      details: entry.details ? sanitizeObject(entry.details) : undefined,
      metadata: entry.metadata ? sanitizeObject(entry.metadata) : undefined
    };

    // Check total size and further truncate if needed
    const jsonStr = JSON.stringify(sanitized);
    if (jsonStr.length > MAX_TOTAL_SIZE) {
      // Aggressively truncate details if still too large
      return {
        ...sanitized,
        details: '[payload too large - truncated]',
        metadata: '[payload too large - truncated]'
      };
    }

    return sanitized;
  }

  /**
   * Process the log queue
   */
  private async processQueue() {
    if (this.isProcessingQueue || this.logQueue.length === 0) return;

    // Check if we have a token - if not, don't try to send logs
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (!token) {
      // User is not authenticated, keep logs in queue for later
      return;
    }

    this.isProcessingQueue = true;
    // Limit batch size to prevent payloads that are too large
    const MAX_BATCH_SIZE = 5;
    const batch = this.logQueue.slice(0, MAX_BATCH_SIZE);
    this.logQueue = this.logQueue.slice(MAX_BATCH_SIZE);

    try {
      // Set a flag to prevent logging API calls for error-logs endpoint
      (window as any).__isLoggingRequest = true;

      // Sanitize all entries to prevent payload size issues
      const sanitizedBatch = batch.map(entry => this.sanitizeLogEntry(entry));

      // Use native fetch to avoid circular dependency with api service
      const apiUrl = process.env.REACT_APP_API_URL || '/api/v1';

      const response = await fetch(`${apiUrl}/endpoints/error-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'batch_log',
          entries: sanitizedBatch
        })
      });

      if (!response.ok) {
        // Don't retry on 403 (permission denied) or 413 (payload too large)
        if (response.status === 403 || response.status === 413) {
          // Silently drop these logs
          (window as any).__isLoggingRequest = false;
          return;
        }
        throw new Error(`Failed to send logs: ${response.status}`);
      }

      (window as any).__isLoggingRequest = false;
    } catch (error: any) {
      (window as any).__isLoggingRequest = false;

      // Don't retry on 403 or 413 errors
      if (error?.message?.includes('403') || error?.message?.includes('413')) {
        return;
      }

      // Failed to send logs - put them back in queue for retry
      this.logQueue = [...batch, ...this.logQueue];
    } finally {
      this.isProcessingQueue = false;

      // If there are more items in queue, process them
      if (this.logQueue.length > 0) {
        setTimeout(() => this.processQueue(), 100);
      }
    }
  }

  /**
   * Queue a log entry for batch processing
   */
  private queueLog(entry: LogEntry) {
    this.logQueue.push({
      ...entry,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Clear existing timer
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    // Process queue after a short delay to batch logs
    this.batchTimer = setTimeout(() => {
      this.processQueue();
    }, 1000); // 1 second delay for batching
  }

  /**
   * Log a debug message
   */
  debug(component: string, message: string, details?: any) {
    if (!this.isComponentEnabled(component)) return;

    this.queueLog({
      component,
      level: 'debug',
      message,
      details
    });

    // Only log to console if explicitly enabled
    // Skip console output for API/TokenManager when logging to prevent visual noise
    const skipConsole = (window as any).__isLoggingRequest && (component === 'API' || component === 'TokenManager');
    if (this.consoleOutputEnabled && this.isDevelopment && !skipConsole) {
      console.log(`[${component}]`, message, details);
    }
  }

  /**
   * Log an info message
   */
  info(component: string, message: string, details?: any) {
    if (!this.isComponentEnabled(component)) return;

    this.queueLog({
      component,
      level: 'info',
      message,
      details
    });

    if (this.consoleOutputEnabled && this.isDevelopment) {
      console.info(`[${component}]`, message, details);
    }
  }

  /**
   * Log a warning
   */
  warning(component: string, message: string, details?: any) {
    if (!this.isComponentEnabled(component)) return;

    this.queueLog({
      component,
      level: 'warning',
      message,
      details
    });

    if (this.consoleOutputEnabled && this.isDevelopment) {
      console.warn(`[${component}]`, message, details);
    }
  }

  /**
   * Log an error - always logs regardless of settings
   */
  error(component: string, message: string, error?: any, showUser: boolean = true) {
    // Always log errors
    const errorDetails = {
      message: error?.message,
      stack: error?.stack,
      response: error?.response?.data,
      status: error?.response?.status
    };

    this.queueLog({
      component,
      level: 'error',
      message,
      details: errorDetails
    });

    if (this.consoleOutputEnabled && this.isDevelopment) {
      console.error(`[${component}]`, message, error);
    }

    // Don't show popup for 401 (session timeout) or 403 (permission denied) errors
    const is401Error = error?.response?.status === 401 ||
                       error?.status === 401 ||
                       message?.includes('401') ||
                       error?.message?.includes('401');

    const is403Error = error?.response?.status === 403 ||
                       error?.status === 403 ||
                       message?.includes('403') ||
                       error?.message?.includes('403');

    // Show user-friendly error message (suppress during logout and for 401/403 errors)
    if (showUser && this.errorNotificationCallback && !this.isLoggingOut && !is401Error && !is403Error) {
      this.errorNotificationCallback('We have encountered an error. Please contact your administrator.');
    }
  }

  /**
   * Log a critical error - always logs and notifies
   */
  critical(component: string, message: string, error?: any) {
    // Always log critical errors
    const errorDetails = {
      message: error?.message,
      stack: error?.stack,
      response: error?.response?.data,
      status: error?.response?.status,
      critical: true
    };

    this.queueLog({
      component,
      level: 'critical',
      message,
      details: errorDetails
    });

    if (this.consoleOutputEnabled && this.isDevelopment) {
      console.error(`[${component}] CRITICAL:`, message, error);
    }

    // Always show user message for critical errors (suppress during logout)
    if (this.errorNotificationCallback && !this.isLoggingOut) {
      this.errorNotificationCallback('We have encountered an error. Please contact your administrator.');
    }
  }

  /**
   * Set logout in progress flag to suppress error notifications
   */
  setLoggingOut(loggingOut: boolean) {
    this.isLoggingOut = loggingOut;
  }

  /**
   * Create a logger instance for a specific component
   */
  createComponentLogger(component: string) {
    return {
      debug: (message: string, details?: any) => this.debug(component, message, details),
      info: (message: string, details?: any) => this.info(component, message, details),
      warning: (message: string, details?: any) => this.warning(component, message, details),
      error: (message: string, error?: any, showUser?: boolean) => this.error(component, message, error, showUser),
      critical: (message: string, error?: any) => this.critical(component, message, error),
      log: (message: string, details?: any) => this.debug(component, message, details), // Alias for debug
    };
  }

  /**
   * Update debug settings for a component
   */
  async updateComponentSetting(component: string, enabled: boolean) {
    try {
      // Use native fetch to avoid circular dependency
      const apiUrl = process.env.REACT_APP_API_URL || '/api/v1';
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      // Only update if we have a token
      if (!token) {
        return;
      }

      const response = await fetch(`${apiUrl}/endpoints/debug-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          component,
          enabled
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          this.debugSettings[component] = enabled;
        }
      }
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Get current debug settings
   */
  getSettings(): ComponentDebugSettings {
    return { ...this.debugSettings };
  }

  /**
   * Flush any pending logs immediately
   */
  async flush() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    await this.processQueue();
  }
}

// Create singleton instance
const errorLogger = new ErrorLoggerService();

// Initialize on load
if (typeof window !== 'undefined') {
  errorLogger.initialize();

  // Flush logs before page unload
  window.addEventListener('beforeunload', () => {
    errorLogger.flush();
  });
}

export default errorLogger;